import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Files, Plus, MagnifyingGlass, Folder, FilePdf, FileDoc, FileXls, FileZip,
  Image, DownloadSimple, Eye, CloudArrowUp, X, Spinner, FolderPlus,
  CaretDown, CaretRight, GearSix, Trash, PencilSimple, DotsThree,
  ArrowRight, CheckSquare,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import SignatureManager from './SignatureManager';
import usePermissions from '../../hooks/usePermissions';
import DocumentsModerationPanel from './documents/DocumentsModerationPanel';
import DocumentVisibilityForm, { DEFAULT_INTERNAL_VISIBILITY, ROLE_LABELS } from './DocumentVisibilityForm';
import DocumentVisibilityModal from './DocumentVisibilityModal';
import DepotPiece from './visas/DepotPiece';
import PlansEnVigueurBureau from './plans/PlansEnVigueurBureau'; // A4 — plans annotés
// Fluidité B2 (P1#17) — catégories proposées selon l'étape du chantier
import { categoriesSelonEtape, GROUPES_CATEGORIES } from '../../lib/libelles';
import { etapeDuChantier } from '../../lib/ongletsChantier';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getFileIcon(doc) {
  const n = (doc.name || '').toLowerCase();
  if (n.endsWith('.pdf')) return FilePdf;
  if (/\.(jpg|jpeg|png|gif|webp|svg)$/.test(n)) return Image;
  if (/\.(xlsx|xls|csv|numbers)$/.test(n)) return FileXls;
  if (/\.(zip|rar|7z)$/.test(n)) return FileZip;
  if (/\.(docx|doc)$/.test(n)) return FileDoc;
  return Files;
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function isPreviewable(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
}

// ==================== MAIN COMPONENT ====================
// Liste déroulante native des catégories au dépôt : celles de l'étape d'abord,
// les autres (les 22 restent accessibles) dans « Autres catégories ».
function OptionsCategories({ categories, etape }) {
  const { proposees, autres } = categoriesSelonEtape(categories, etape);
  if (!autres.length) return proposees.map((c) => <option key={c.code} value={c.code}>{c.nom}</option>);
  return (
    <>
      <optgroup label={GROUPES_CATEGORIES.proposees}>
        {proposees.map((c) => <option key={c.code} value={c.code}>{c.nom}</option>)}
      </optgroup>
      <optgroup label={GROUPES_CATEGORIES.autres}>
        {autres.map((c) => <option key={c.code} value={c.code}>{c.nom}</option>)}
      </optgroup>
    </>
  );
}

// Fluidité B2 — `chantier` : pour l'étape (catégories proposées) ; `plansSeulement` :
// Chantier › Plans n'affiche que les plans en vigueur (lot A4) et leur dépôt.
export default function ProjectDocuments({ chantierId, userRole, chantier = null, plansSeulement = false }) {
  const etape = chantier ? etapeDuChantier(chantier) : null;
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [newDoc, setNewDoc] = useState({ name: '', type: 'autre', url: '' });
  const [selected, setSelected] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null);  const [catContextMenu, setCatContextMenu] = useState(null);
  const [renamingDoc, setRenamingDoc] = useState(null);
  const [renamingCat, setRenamingCat] = useState(null);
  const [moveDialog, setMoveDialog] = useState(null);
  const [visibilityDoc, setVisibilityDoc] = useState(null);  // Iter63b-bis-fix Feature B
  const [deleteCatDialog, setDeleteCatDialog] = useState(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [dragOverCat, setDragOverCat] = useState(null);
  const [vueDocs, setVueDocs] = useState(plansSeulement ? 'plans' : 'documents'); // A4 — 'documents' | 'plans'

  const canEdit = ['admin', 'architect', 'collaborateur'].includes(userRole); // RP32 — classer, renommer (server.py ROLES_CABINET)
  // Iter63b-bis — modération réservée archi/moe_principal du chantier
  const { perms, role_projet: roleProjet } = usePermissions(chantierId);
  const canModerate = !!perms?.can_validate_documents;
  // RP51 / D24 — zone de dépôt par lot et avec indice pour les partenaires (visible
  // tout de suite par la MOE) et les entreprises (document d'exécution à viser).
  const canDeposit = !canEdit && ['consultant', 'entreprise'].includes(roleProjet);

  useEffect(() => { fetchDocuments(); fetchCategories(); }, [chantierId]);
  useEffect(() => {
    const handler = () => { setContextMenu(null); setCatContextMenu(null); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const fetchCategories = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/categories-documents?chantier_id=${chantierId}`, { withCredentials: true }); setCategories(data); } catch { /* */ }
  };
  const fetchDocuments = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/documents/${chantierId}`, { withCredentials: true }); setDocuments(data); } catch { /* */ } finally { setLoading(false); }
  };
  const refresh = () => { fetchDocuments(); fetchCategories(); };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API_URL}/api/documents`, { ...newDoc, chantier_id: chantierId }, { withCredentials: true }); toast.success('Document ajouté'); setDocDialogOpen(false); setNewDoc({ name: '', type: 'autre', url: '' }); fetchDocuments(); } catch { toast.error("Erreur"); }
  };

  // ===== Document actions =====
  const updateDoc = async (docId, updates) => {
    try { await axios.put(`${API_URL}/api/documents/${docId}`, updates, { withCredentials: true }); fetchDocuments(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const deleteDoc = async (docId) => {
    try { await axios.delete(`${API_URL}/api/documents/${docId}`, { withCredentials: true }); toast.success('Supprimé'); fetchDocuments(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const bulkMove = async (targetType) => {
    for (const id of selected) { await updateDoc(id, { type: targetType }); }
    toast.success(`${selected.size} fichier(s) déplacé(s)`); setSelected(new Set());
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Supprimer ${selected.size} fichier(s) ?`)) return;
    for (const id of selected) { await deleteDoc(id); }
    setSelected(new Set());
  };

  // ===== Category actions =====
  const renameCat = async (catId, newName) => {
    try { await axios.put(`${API_URL}/api/categories-documents/${catId}`, { nom: newName }, { withCredentials: true }); toast.success('Renommé'); fetchCategories(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const deleteCat = async (catId, reclasserVers) => {
    try {
      const url = reclasserVers ? `${API_URL}/api/categories-documents/${catId}?reclasser_vers=${reclasserVers}` : `${API_URL}/api/categories-documents/${catId}`;
      await axios.delete(url, { withCredentials: true });
      toast.success('Catégorie supprimée'); setDeleteCatDialog(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const createCat = async (nom, couleur) => {
    try { await axios.post(`${API_URL}/api/categories-documents`, { nom, couleur, chantier_id: chantierId }, { withCredentials: true }); toast.success('Dossier créé'); setNewFolderOpen(false); fetchCategories(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // ===== Drag & Drop =====
  const handleDragStart = (e, docId) => { e.dataTransfer.setData('text/plain', docId); };
  const handleDragOver = (e, catCode) => { e.preventDefault(); setDragOverCat(catCode); };
  const handleDragLeave = () => { setDragOverCat(null); };
  const handleDrop = async (e, catCode) => {
    e.preventDefault(); setDragOverCat(null);
    const docId = e.dataTransfer.getData('text/plain');
    if (docId) { await updateDoc(docId, { type: catCode }); toast.success('Fichier déplacé'); }
  };

  // ===== Selection =====
  const toggleSelect = (docId) => {
    setSelected(prev => { const n = new Set(prev); n.has(docId) ? n.delete(docId) : n.add(docId); return n; });
  };

  // ===== Filtering & Grouping =====
  const filteredDocs = documents.filter(d => {
    const ms = (d.name || '').toLowerCase().includes(search.toLowerCase());
    const mt = typeFilter === 'all' || d.type === typeFilter;
    return ms && mt;
  });
  const catMap = {}; categories.forEach(c => { catMap[c.code] = c; });
  const grouped = {}; filteredDocs.forEach(d => { const c = d.type || 'autre'; if (!grouped[c]) grouped[c] = []; grouped[c].push(d); });
  const orderedCodes = categories.map(c => c.code).filter(c => grouped[c]);
  Object.keys(grouped).forEach(c => { if (!orderedCodes.includes(c)) orderedCodes.push(c); });

  // Sous-groupement par date pour la catégorie "suivi_chantier"
  const groupByDate = (docs) => {
    const byDate = {};
    docs.forEach(d => {
      const key = d.date_terrain || (d.uploaded_at ? d.uploaded_at.slice(0, 10) : 'unknown');
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(d);
    });
    return Object.entries(byDate).sort((a, b) => b[0].localeCompare(a[0])); // plus récent en premier
  };
  const formatDateFr = (iso) => {
    if (!iso || iso === 'unknown') return 'Date inconnue';
    try {
      const d = new Date(iso + 'T00:00:00');
      const today = new Date(); today.setHours(0,0,0,0);
      const yest = new Date(today); yest.setDate(today.getDate() - 1);
      if (d.getTime() === today.getTime()) return "Aujourd'hui";
      if (d.getTime() === yest.getTime()) return 'Hier';
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return iso; }
  };

  const toggle = (code) => setCollapsed(p => ({ ...p, [code]: !p[code] }));
  // A4 — « Plans en vigueur » : plans par lot, annotation (épingles d'actions), export PDF
  const voitPlans = ['archi', 'moe_principal', 'moe_membre', 'consultant', 'entreprise'].includes(roleProjet);
  const ongletsDocs = voitPlans && !plansSeulement && (
    <div className="flex gap-1 mb-4 border-b border-[#E0E0DB]" role="tablist" data-testid="documents-onglets">
      {[['documents', 'Tous les documents'], ['plans', 'Plans en vigueur']].map(([k, l]) => (
        <button key={k} role="tab" aria-selected={vueDocs === k} onClick={() => setVueDocs(k)}
          className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 ${vueDocs === k ? 'border-[#C4773B] text-[#2C2C2C]' : 'border-transparent text-[#6B6B6B] hover:text-[#2C2C2C]'}`}
          data-testid={`documents-onglet-${k}`}>{l}</button>
      ))}
    </div>
  );
  if ((voitPlans || plansSeulement) && vueDocs === 'plans') {
    return (
      <div>
        {ongletsDocs}
        {canEdit && <UploadZone chantierId={chantierId} categories={categories} etape={etape} onUploaded={fetchDocuments} initialType="plans" key="depot-plans" />}
        <PlansEnVigueurBureau chantierId={chantierId} key={documents.length} />
      </div>
    );
  }

  return (
    <div>
      {ongletsDocs}
      <DocumentsModerationPanel
        chantierId={chantierId}
        canModerate={canModerate}
        onChange={fetchDocuments}
      />

      {canEdit && <UploadZone chantierId={chantierId} categories={categories} etape={etape} onUploaded={fetchDocuments} />}
      {canDeposit && <DepotPiece chantierId={chantierId} roleProjet={roleProjet} onDeposited={fetchDocuments} />}

      {/* Selection Bar */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-20 bg-[#1E3A5F] text-white px-6 py-3 flex items-center justify-between rounded-lg mb-4" data-testid="selection-bar">
          <span className="text-sm font-medium">{selected.size} sélectionné(s)</span>
          <div className="flex items-center gap-2">
            <Select onValueChange={v => bulkMove(v)}>
              <SelectTrigger className="h-8 w-40 rounded-none border-white/30 bg-transparent text-white text-xs"><SelectValue placeholder="Déplacer vers..." /></SelectTrigger>
              <SelectContent>{categories.map(c => <SelectItem key={c.code} value={c.code}>{c.nom}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 rounded-none h-8 gap-1" onClick={bulkDelete}><Trash size={14} />Supprimer</Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 rounded-none h-8" onClick={() => setSelected(new Set())}><X size={14} /></Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-none border-[#E0E0DB]" data-testid="doc-search-input" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44 rounded-none border-[#E0E0DB]" data-testid="doc-type-filter"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            {categories.map(c => <SelectItem key={c.code} value={c.code}>{c.nom}</SelectItem>)}
          </SelectContent>
        </Select>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none gap-1 text-sm" onClick={() => setNewFolderOpen(true)} data-testid="new-folder-btn"><FolderPlus size={16} />Nouveau dossier</Button>
            <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
              <DialogTrigger asChild><Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-1 text-sm" data-testid="add-document-button"><Plus size={16} />Lien externe</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Ajouter un lien externe</DialogTitle></DialogHeader>
                <form onSubmit={handleAddDocument} className="space-y-4">
                  <div><Label>Nom</Label><Input value={newDoc.name} onChange={e => setNewDoc({ ...newDoc, name: e.target.value })} required className="rounded-none" /></div>
                  <div><Label>Catégorie</Label><Select value={newDoc.type} onValueChange={v => setNewDoc({ ...newDoc, type: v })}><SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger><SelectContent>{(() => { const { proposees, autres } = categoriesSelonEtape(categories, etape); return autres.length ? (<><SelectGroup><SelectLabel>{GROUPES_CATEGORIES.proposees}</SelectLabel>{proposees.map(c => <SelectItem key={c.code} value={c.code}>{c.nom}</SelectItem>)}</SelectGroup><SelectGroup><SelectLabel>{GROUPES_CATEGORIES.autres}</SelectLabel>{autres.map(c => <SelectItem key={c.code} value={c.code}>{c.nom}</SelectItem>)}</SelectGroup></>) : proposees.map(c => <SelectItem key={c.code} value={c.code}>{c.nom}</SelectItem>); })()}</SelectContent></Select></div>
                  <div><Label>URL</Label><Input value={newDoc.url} onChange={e => setNewDoc({ ...newDoc, url: e.target.value })} className="rounded-none" /></div>
                  <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDocDialogOpen(false)} className="rounded-none">Annuler</Button><Button type="submit" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Ajouter</Button></div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Documents */}
      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : orderedCodes.length === 0 ? (
        <div className="card-kroquis text-center py-16"><Files size={64} className="mx-auto text-[#E0E0DB] mb-4" /><h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">Aucun document</h3>{/* RP02 — « Glissez des fichiers ci-dessus » seulement si la zone de dépôt est affichée */}<p className="text-[#6B6B6B]" data-testid="documents-empty-hint">{canEdit ? 'Glissez des fichiers ci-dessus' : "Aucun document partagé avec vous pour l'instant."}</p></div>
      ) : (
        <div className="space-y-2">
          {orderedCodes.map(code => {
            const cat = catMap[code] || { nom: code, couleur: '#6B6B6B', id: null };
            const docs = grouped[code];
            const isCol = collapsed[code];
            const isDragOver = dragOverCat === code;
            return (
              <div
                key={code}
                className={`border bg-white overflow-hidden transition-all ${isDragOver ? 'border-[#C4773B] border-dashed bg-[#C4773B]/5' : 'border-[#E0E0DB]'}`}
                onDragOver={e => handleDragOver(e, code)} onDragLeave={handleDragLeave} onDrop={e => handleDrop(e, code)}
                data-testid={`category-${code}`}
              >
                {/* Category header */}
                <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#FAFAF8] transition-colors">
                  <button className="flex items-center gap-3 flex-1 text-left" onClick={() => toggle(code)}>
                    {isCol ? <CaretRight size={14} className="text-[#9A9A9A]" /> : <CaretDown size={14} className="text-[#9A9A9A]" />}
                    <div className="w-7 h-7 flex items-center justify-center rounded" style={{ background: cat.couleur + '18', color: cat.couleur }}><Folder size={14} weight="fill" /></div>
                    {renamingCat === code ? (
                      <InlineRename defaultValue={cat.nom} onSave={v => { renameCat(cat.id, v); setRenamingCat(null); }} onCancel={() => setRenamingCat(null)} />
                    ) : (
                      <span className="font-semibold text-[#2C2C2C] text-sm">{cat.nom}</span>
                    )}
                    <span className="text-xs text-[#9A9A9A] bg-[#F5F5F3] px-2 py-0.5 rounded-full">{docs.length}</span>
                  </button>
                  {canEdit && cat.id && (
                    <div className="relative">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-none" onClick={e => { e.stopPropagation(); setCatContextMenu(catContextMenu === code ? null : code); }} data-testid={`cat-menu-${code}`}><DotsThree size={16} /></Button>
                      {catContextMenu === code && (
                        <div className="absolute right-0 top-8 bg-white border border-[#E0E0DB] rounded-lg shadow-lg py-1 min-w-[160px] z-50" onClick={e => e.stopPropagation()}>
                          <button className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F3] flex items-center gap-2" onClick={() => { setRenamingCat(code); setCatContextMenu(null); }}><PencilSimple size={14} />Renommer</button>
                          <button className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F3] flex items-center gap-2 text-[#C53030]" onClick={() => { setDeleteCatDialog(cat); setCatContextMenu(null); }}><Trash size={14} />Supprimer</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Documents list */}
                {!isCol && (
                  code === 'suivi_chantier' ? (
                    <div className="border-t border-[#E0E0DB]">
                      {groupByDate(docs).map(([dateKey, dateDocs]) => (
                        <div key={dateKey} className="border-b border-[#F0F0ED] last:border-b-0">
                          <div className="px-4 py-1.5 bg-[#FAFAF8] text-xs font-semibold text-[#6B4A24] uppercase tracking-wide flex items-center gap-2" data-testid={`terrain-date-${dateKey}`}>
                            <span className="w-1.5 h-1.5 bg-[#C4773B] rounded-full" />
                            {formatDateFr(dateKey)}
                            <span className="text-[#9A9A9A] font-normal normal-case">· {dateDocs.length} élément{dateDocs.length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="divide-y divide-[#F0F0ED]">
                            {dateDocs.map(doc => renderDocRow(doc, {
                              chantierId, selected, toggleSelect, setPreviewDoc, setRenamingDoc, renamingDoc,
                              canEdit, setContextMenu, contextMenu, setMoveDialog, deleteDoc, updateDoc,
                              handleDragStart, setVisibilityDoc, canModerate,
                            }))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                  <div className="border-t border-[#E0E0DB] divide-y divide-[#F0F0ED]">
                    {docs.map(doc => renderDocRow(doc, {
                      chantierId, selected, toggleSelect, setPreviewDoc, setRenamingDoc, renamingDoc,
                      canEdit, setContextMenu, contextMenu, setMoveDialog, deleteDoc, updateDoc,
                      handleDragStart, setVisibilityDoc, canModerate,
                    }))}
                  </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {previewDoc && <DocumentPreview document={previewDoc} chantierId={chantierId} onClose={() => setPreviewDoc(null)} />}
      {moveDialog && <MoveDialog doc={moveDialog} categories={categories} onMove={(type) => { updateDoc(moveDialog.id, { type }); toast.success('Déplacé'); setMoveDialog(null); }} onClose={() => setMoveDialog(null)} />}
      {visibilityDoc && (
        <DocumentVisibilityModal
          doc={visibilityDoc}
          chantierId={chantierId}
          open={!!visibilityDoc}
          onClose={() => setVisibilityDoc(null)}
          onUpdated={fetchDocuments}
        />
      )}
      {deleteCatDialog && <DeleteCatDialog cat={deleteCatDialog} categories={categories} grouped={grouped} onDelete={deleteCat} onClose={() => setDeleteCatDialog(null)} />}
      {newFolderOpen && <NewFolderDialog onSave={createCat} onClose={() => setNewFolderOpen(false)} />}
    </div>
  );
}


// ==================== SUB-COMPONENTS ====================

function renderDocRow(doc, ctx) {
  const {
    chantierId, selected, toggleSelect, setPreviewDoc, setRenamingDoc, renamingDoc,
    canEdit, setContextMenu, contextMenu, setMoveDialog, deleteDoc, updateDoc,
    handleDragStart, setVisibilityDoc, canModerate,
  } = ctx;
  const DocIcon = doc.stored_name ? getFileIcon(doc) : Files;
  const isUp = !!doc.stored_name;
  const fileUrl = isUp ? `${API_URL}/api/chantiers/${chantierId}/files/${doc.stored_name}` : doc.url;
  const canPrev = isUp && isPreviewable(doc.name);
  const isSel = selected.has(doc.id);
  return (
    <div
      key={doc.id}
      className={`flex items-center gap-3 px-4 py-2.5 group transition-colors ${isSel ? 'bg-[#1E3A5F]/8' : 'hover:bg-[#FAFAF8]'} ${canPrev ? 'cursor-pointer' : ''}`}
      draggable={canEdit} onDragStart={e => handleDragStart(e, doc.id)}
      onClick={() => canPrev && !renamingDoc && setPreviewDoc(doc)}
      data-testid={`doc-card-${doc.id}`}
    >
      {canEdit && (
        <input type="checkbox" checked={isSel} onChange={e => { e.stopPropagation(); toggleSelect(doc.id); }} onClick={e => e.stopPropagation()} className="w-4 h-4 accent-[#1E3A5F] cursor-pointer" data-testid={`doc-select-${doc.id}`} />
      )}
      <DocIcon size={18} className="text-[#6B6B6B] flex-shrink-0" />
      <div className="flex-1 min-w-0" onDoubleClick={e => { if (canEdit) { e.stopPropagation(); setRenamingDoc(doc.id); } }}>
        {renamingDoc === doc.id ? (
          <InlineRename defaultValue={doc.name} onSave={v => { updateDoc(doc.id, { name: v }); setRenamingDoc(null); }} onCancel={() => setRenamingDoc(null)} />
        ) : (
          <>
            <p className="text-sm font-medium text-[#2C2C2C] truncate flex items-center gap-1.5">
              {doc.name}
              {doc.source === 'terrain' && <span className="text-[9px] uppercase tracking-wide text-[#C4773B] font-semibold bg-[#FDF6EC] px-1.5 py-0.5 rounded">terrain</span>}
              {doc.indice && <span className="text-[9px] uppercase tracking-wide text-[#2D6A4F] font-semibold bg-[#E8F5E9] px-1.5 py-0.5 rounded" data-testid={`doc-indice-${doc.id}`}>Ind. {doc.indice}</span>}
              {doc.moderation_status === 'pending' && (
                <span className="text-[9px] uppercase tracking-wide text-amber-800 font-semibold bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`doc-mod-pending-${doc.id}`}>
                  En modération
                </span>
              )}
              {doc.moderation_status === 'rejected' && (
                <span
                  className="text-[9px] uppercase tracking-wide text-rose-800 font-semibold bg-rose-100 px-1.5 py-0.5 rounded"
                  title={doc.moderation_note || 'Rejeté'}
                  data-testid={`doc-mod-rejected-${doc.id}`}
                >
                  Rejeté
                </span>
              )}
            </p>
            <p className="text-xs text-[#9A9A9A]">
              {doc.uploaded_by_name && <>{doc.uploaded_by_name} &bull; </>}
              {doc.file_size ? formatSize(doc.file_size) + ' \u2022 ' : ''}
              {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
            </p>
          </>
        )}
      </div>
      <div className="flex items-center gap-1.5 mr-1" onClick={e => e.stopPropagation()}>
        {/* Sprint D #11 — Badges visibilité */}
        <DocVisibilityBadges
          doc={doc}
          canModerate={canModerate}
          onClickEdit={() => setVisibilityDoc(doc)}
        />
      </div>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
        {canPrev && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setPreviewDoc(doc)} data-testid={`doc-view-${doc.id}`}><Eye size={14} /></Button>}
        {fileUrl && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => window.open(fileUrl, '_blank')} data-testid={`doc-download-${doc.id}`}><DownloadSimple size={14} /></Button>}
        {canEdit && (
          <div className="relative">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); setContextMenu(contextMenu === doc.id ? null : doc.id); }} data-testid={`doc-menu-${doc.id}`}><DotsThree size={16} /></Button>
            {contextMenu === doc.id && (
              <div className="absolute right-0 top-8 bg-white border border-[#E0E0DB] rounded-lg shadow-lg py-1 min-w-[180px] z-50" onClick={e => e.stopPropagation()}>
                <button className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F3] flex items-center gap-2" onClick={() => { setRenamingDoc(doc.id); setContextMenu(null); }}><PencilSimple size={14} />Renommer</button>
                <button className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F3] flex items-center gap-2" onClick={() => { setMoveDialog(doc); setContextMenu(null); }}><ArrowRight size={14} />Déplacer vers...</button>
                {canModerate && setVisibilityDoc && (
                  <button
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F3] flex items-center gap-2"
                    onClick={() => { setVisibilityDoc(doc); setContextMenu(null); }}
                    data-testid={`doc-visibility-${doc.id}`}
                  >
                    <Eye size={14} />Visibilité
                  </button>
                )}
                <button className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F3] flex items-center gap-2 text-[#C53030]" onClick={() => { if (window.confirm(`Supprimer "${doc.name}" ?`)) { deleteDoc(doc.id); } setContextMenu(null); }}><Trash size={14} />Supprimer</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// Sprint D #11 — Badges visibilité affichés sur chaque ligne document
const SHORT_BADGES = {
  archi: { l: 'Archi', cls: 'bg-[#1E3A5F] text-white' },
  moe_principal: { l: 'MOE', cls: 'bg-[#2D6A4F] text-white' },
  moe_membre: { l: 'MOE', cls: 'bg-[#6B8F71] text-white' },
  consultant: { l: 'AMO', cls: 'bg-[#8B6F47] text-white' },
  entreprise: { l: 'Entreprise', cls: 'bg-[#C75B39] text-white' },
  client: { l: 'Client', cls: 'bg-[#6B6B6B] text-white' },
};

function DocVisibilityBadges({ doc, canModerate, onClickEdit }) {
  const visTo = Array.isArray(doc.visible_to) && doc.visible_to.length > 0
    ? doc.visible_to
    : DEFAULT_INTERNAL_VISIBILITY;
  // Dédup MOE (principal vs membre)
  const moeShown = visTo.includes('moe_principal') || visTo.includes('moe_membre');
  const display = [];
  if (visTo.includes('archi')) display.push({ key: 'archi', ...SHORT_BADGES.archi });
  if (moeShown) display.push({ key: 'moe', l: 'MOE', cls: 'bg-[#2D6A4F] text-white' });
  if (visTo.includes('consultant')) display.push({ key: 'consultant', ...SHORT_BADGES.consultant });
  if (visTo.includes('entreprise')) display.push({ key: 'entreprise', ...SHORT_BADGES.entreprise });
  if (visTo.includes('client')) display.push({ key: 'client', ...SHORT_BADGES.client });
  if (!display.length) return null;
  const handleClick = (e) => {
    if (!canModerate || !onClickEdit) return;
    e.stopPropagation();
    onClickEdit();
  };
  return (
    <div
      className={`flex items-center gap-0.5 ${canModerate && onClickEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
      onClick={handleClick}
      title={canModerate ? 'Cliquer pour éditer la visibilité' : 'Visibilité du document'}
      data-testid={`doc-visibility-badges-${doc.id}`}
    >
      {display.map((b) => (
        <span
          key={b.key}
          className={`text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 ${b.cls}`}
          data-testid={`doc-visibility-badge-${doc.id}-${b.key}`}
        >
          {b.l}
        </span>
      ))}
    </div>
  );
}

function InlineRename({ defaultValue, onSave, onCancel }) {
  const [val, setVal] = useState(defaultValue);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);  const save = () => { if (val.trim() && val !== defaultValue) onSave(val.trim()); else onCancel(); };
  return (
    <input
      ref={ref} value={val} onChange={e => setVal(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onCancel(); }}
      onBlur={save} onClick={e => e.stopPropagation()}
      className="text-sm font-medium border border-[#C4773B] rounded px-1.5 py-0.5 outline-none w-full max-w-xs"
      data-testid="inline-rename-input"
    />
  );
}


function DocumentPreview({ document, chantierId, onClose }) {
  const fileUrl = document.stored_name ? `${API_URL}/api/chantiers/${chantierId}/files/${document.stored_name}` : document.url;
  const ext = (document.name || '').split('.').pop().toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
  const isPdf = ext === 'pdf';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose} data-testid="document-preview-overlay">
      <div className="bg-white flex flex-col overflow-hidden" style={{ width: '90vw', height: '90vh', maxWidth: 1200, borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E0E0DB]">
          <h3 className="text-base font-semibold text-[#2C2C2C] truncate flex-1 mr-4">{document.name}</h3>
          <div className="flex items-center gap-2">
            {fileUrl && <a href={fileUrl} download={document.name} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-none gap-1"><DownloadSimple size={14} />Télécharger</Button></a>}
            <Button variant="ghost" size="sm" className="rounded-none h-8 w-8 p-0" onClick={onClose} data-testid="preview-close"><X size={16} /></Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center bg-[#F5F5F3] p-4">
          {isPdf && fileUrl && <iframe src={fileUrl} title={document.name} style={{ width: '100%', height: '100%', border: 'none' }} />}
          {isImage && fileUrl && <img src={fileUrl} alt={document.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
          {(!isPdf && !isImage) && (
            <div className="text-center py-12">
              <Files size={48} className="mx-auto text-[#E0E0DB] mb-4" />
              <p className="text-[#6B6B6B] mb-4">Prévisualisation non disponible (.{ext})</p>
              {fileUrl && <a href={fileUrl} download={document.name} target="_blank" rel="noreferrer"><Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-1"><DownloadSimple size={14} />Télécharger</Button></a>}
            </div>
          )}
        </div>
        {/* Signatures */}
        <div className="border-t border-[#E0E0DB] p-4 bg-white max-h-[30vh] overflow-y-auto">
          <SignatureManager
            chantierId={chantierId}
            documentType="document"
            documentId={document.id}
            documentTitle={document.name}
          />
        </div>
      </div>
    </div>
  );
}
export { DocumentPreview };


function MoveDialog({ doc, categories, onMove, onClose }) {
  const [target, setTarget] = useState('');
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Déplacer "{doc.name}"</DialogTitle></DialogHeader>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger className="rounded-none" data-testid="move-target-select"><SelectValue placeholder="Choisir un dossier..." /></SelectTrigger>
          <SelectContent>{categories.filter(c => c.code !== doc.type).map(c => <SelectItem key={c.code} value={c.code}>{c.nom}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
          <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" disabled={!target} onClick={() => onMove(target)} data-testid="move-confirm">Déplacer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function DeleteCatDialog({ cat, categories, grouped, onDelete, onClose }) {
  const [reclasser, setReclasser] = useState('autre');
  const count = (grouped[cat.code] || []).length;
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Supprimer "{cat.nom}"</DialogTitle></DialogHeader>
        {count > 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-[#6B6B6B]">{count} document(s) dans cette catégorie. Reclasser vers :</p>
            <Select value={reclasser} onValueChange={setReclasser}>
              <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.filter(c => c.code !== cat.code).map(c => <SelectItem key={c.code} value={c.code}>{c.nom}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : (
          <p className="text-sm text-[#6B6B6B]">Ce dossier est vide. Le supprimer ?</p>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
          <Button className="bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-none" onClick={() => onDelete(cat.id, count > 0 ? reclasser : null)} data-testid="delete-cat-confirm">Supprimer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function NewFolderDialog({ onSave, onClose }) {
  const [nom, setNom] = useState('');
  const [couleur, setCouleur] = useState('#C4773B');
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Nouveau dossier</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom</Label><Input value={nom} onChange={e => setNom(e.target.value)} placeholder="Ex: Plans modificatifs" className="rounded-none mt-1" autoFocus data-testid="new-folder-name" /></div>
          <div className="flex items-center gap-3">
            <Label>Couleur</Label>
            <input type="color" value={couleur} onChange={e => setCouleur(e.target.value)} className="w-9 h-9 p-0 border border-[#E0E0DB] cursor-pointer" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
          <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" disabled={!nom.trim()} onClick={() => onSave(nom.trim(), couleur)} data-testid="new-folder-submit">Créer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// B10 (PARCOURS 03 §17) — la diffusion choisie (dont « Entreprises ») est conservée
// d'un dépôt à l'autre, par chantier, sur ce poste ; elle reste visible sous la zone.
const cleVisibilite = (chantierId) => `kroquis_depot_visibilite_${chantierId}`;
function lireVisibilite(chantierId) {
  try {
    const v = JSON.parse(window.localStorage.getItem(cleVisibilite(chantierId)) || 'null');
    if (v && Array.isArray(v.visibleTo)) return v;
  } catch { /* stockage indisponible */ }
  return null;
}
function ecrireVisibilite(chantierId, v) {
  try { window.localStorage.setItem(cleVisibilite(chantierId), JSON.stringify(v)); } catch { /* */ }
}

function UploadZone({ chantierId, categories, etape = null, onUploaded, initialType = 'autre' }) {
  const memo = lireVisibilite(chantierId);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState(initialType);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [visibleTo, setVisibleToState] = useState(memo?.visibleTo || ['archi', 'moe_principal', 'moe_membre', 'consultant']);
  const [visibleToSpecificUsers, setVisibleToSpecificUsersState] = useState(memo?.visibleToSpecificUsers || []);
  const [visibilitePerso, setVisibilitePerso] = useState(!!memo);
  const [advancedNote, setAdvancedNote] = useState('');
  // A4 — dépôt de plans : famille (nouvel indice de…) + indice obligatoire + lot
  const [plansEnAttente, setPlansEnAttente] = useState(null); // [{ file, cle, indice, lot }]
  const [famillesPlans, setFamillesPlans] = useState([]);
  const [lotsMarche, setLotsMarche] = useState([]);
  const inputRef = useRef(null);

  // Le formulaire de diffusion passe des fonctions de mise à jour (prev => …) : on les
  // applique, puis la diffusion choisie est mémorisée pour les prochains dépôts.
  const setVisibleTo = (v) => { setVisibleToState(v); setVisibilitePerso(true); };
  const setVisibleToSpecificUsers = (v) => { setVisibleToSpecificUsersState(v); setVisibilitePerso(true); };
  useEffect(() => {
    if (visibilitePerso) ecrireVisibilite(chantierId, { visibleTo, visibleToSpecificUsers });
  }, [chantierId, visibilitePerso, visibleTo, visibleToSpecificUsers]);
  const ajouterVisibilite = (fd) => {
    if (showAdvanced || visibilitePerso) {
      fd.append('visible_to', JSON.stringify(visibleTo));
      fd.append('visible_to_specific_users', JSON.stringify(visibleToSpecificUsers));
      if (advancedNote) fd.append('moderation_note', advancedNote);
    }
  };

  const preparerPlans = async (files) => {
    try {
      const [{ data }, lotsR] = await Promise.all([
        axios.post(`${API_URL}/api/chantiers/${chantierId}/plans-annotes/depot/preparer`, { noms: files.map((f) => f.name) }, { withCredentials: true }),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setFamillesPlans(data.plans || []);
      setLotsMarche(lotsR.data || []);
      setPlansEnAttente(files.map((file, i) => {
        const f = (data.fichiers || [])[i] || {};
        return { file, cle: f.cle || 'nouveau', indice: f.indice_propose || 'A', lot: '' };
      }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Préparation du dépôt impossible'); }
  };

  const indiceDejaDepose = (p) => {
    const fam = famillesPlans.find((f) => f.cle === p.cle);
    return !!fam && (fam.indices_existants || []).includes(String(p.indice || '').trim().toUpperCase());
  };
  const deposerPlans = async () => {
    if (plansEnAttente.some((p) => !String(p.indice || '').trim())) { toast.error("Indiquez l'indice de chaque plan"); return; }
    if (plansEnAttente.some(indiceDejaDepose)) { toast.error('Un indice est déjà déposé pour ce plan : indiquez le nouvel indice'); return; }
    setUploading(true);
    let ok = 0;
    for (const p of plansEnAttente) {
      const fd = new FormData();
      fd.append('file', p.file);
      fd.append('doc_type', 'plans');
      fd.append('indice', p.indice.trim());
      fd.append('plan_famille', p.cle || 'nouveau');
      if (p.cle === 'nouveau' && p.lot) fd.append('lot_id', p.lot);
      fd.append('force_duplicate', 'true');
      ajouterVisibilite(fd);
      try {
        await axios.post(`${API_URL}/api/chantiers/${chantierId}/upload`, fd, { withCredentials: true });
        ok += 1;
      } catch (e) { toast.error(`${p.file.name} : ${e.response?.data?.detail || 'échec du dépôt'}`); }
    }
    setUploading(false);
    if (ok) { toast.success(`${ok} plan(s) déposé(s) : le dernier indice est en vigueur`); onUploaded(); }
    if (ok === plansEnAttente.length) setPlansEnAttente(null);
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    if (docType === 'plans') { preparerPlans(files); return; }
    setUploading(true);
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    fd.append('doc_type', docType);
    // Iter63b-bis-fix Feature A — paramètres avancés (B10 : conservés d'un dépôt à l'autre)
    ajouterVisibilite(fd);
    try {
      const res = await fetch(`${API_URL}/api/chantiers/${chantierId}/upload-multiple`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Échec'); }
      const data = await res.json();
      toast.success(`${data.uploaded} fichier(s) uploadé(s)`);
      onUploaded();
      setAdvancedNote('');
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const majPlan = (i, champs) => setPlansEnAttente((l) => l.map((p, j) => {
    if (j !== i) return p;
    const n = { ...p, ...champs };
    if (champs.cle !== undefined) {
      const fam = famillesPlans.find((f) => f.cle === champs.cle);
      n.indice = fam ? fam.indice_suivant : 'A';
    }
    return n;
  }));
  const libelleDiffusion = visibleTo.map((r) => ROLE_LABELS[r] || r).join(', ');

  return (
    <div className="mb-6">
      <div
        className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-all ${dragging ? 'border-[#C4773B] bg-[#C4773B]/5' : 'border-[#E0E0DB] hover:border-[#C4773B]/50'}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={e => { if (e.currentTarget.contains(e.relatedTarget)) return; setDragging(false); }}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => !uploading && inputRef.current?.click()}
        data-testid="upload-zone"
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2"><Spinner size={28} className="text-[#C4773B] animate-spin" /><p className="text-sm text-[#6B6B6B]">Upload...</p></div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <CloudArrowUp size={32} className="text-[#6B6B6B]" />
            <p className="text-sm font-medium text-[#2C2C2C]">Glissez vos fichiers ici ou cliquez</p>
            <p className="text-xs text-[#9A9A9A]">{docType === 'plans' ? 'Plans : vous indiquerez ensuite l\'indice de chaque plan' : 'Tous formats (max 50 Mo)'}</p>
          </div>
        )}
        <input ref={inputRef} type="file" multiple onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} data-testid="upload-file-input" />
        <div className="flex items-center justify-center gap-2 mt-3" onClick={e => e.stopPropagation()}>
          <span className="text-xs text-[#6B6B6B]">Catégorie :</span>
          <select value={docType} onChange={e => setDocType(e.target.value)} className="text-xs px-2 py-1 border border-[#E0E0DB] rounded bg-white" data-testid="upload-type-select">
            <OptionsCategories categories={categories} etape={etape} />
          </select>
        </div>
        <p className="text-xs text-[#6B6B6B] mt-2" onClick={e => e.stopPropagation()} data-testid="upload-diffusion-resume">
          Visible par : <strong className="text-[#2C2C2C]">{libelleDiffusion || 'vous seul'}</strong>
          {visibilitePerso && <span className="text-[#9A9A9A]"> (conservé pour vos prochains dépôts)</span>}
        </p>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowAdvanced(!showAdvanced); }}
          className="text-xs text-[#6B6B6B] hover:text-[#C4773B] mt-2 flex items-center gap-1 mx-auto"
          data-testid="toggle-advanced-upload"
        >
          {showAdvanced ? '▲' : '▶'} Paramètres avancés
        </button>
      </div>
      {plansEnAttente && (
        <div className="mt-3 border border-[#E0E0DB] bg-white p-4" data-testid="upload-plans-panel">
          <h4 className="text-sm font-semibold text-[#2C2C2C] mb-1">Dépôt de plans</h4>
          <p className="text-xs text-[#6B6B6B] mb-3">Le dernier indice d'un plan devient « En vigueur » ; les anciens restent consultables, barrés. Les actions placées sur le plan suivent le nouvel indice.</p>
          <div className="space-y-3">
            {plansEnAttente.map((p, i) => (
              <div key={`${p.file.name}-${i}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border-b border-[#F0F0ED] pb-3" data-testid={`upload-plan-${i}`}>
                <div className="md:col-span-3 text-sm font-medium text-[#2C2C2C] truncate" title={p.file.name}>{p.file.name}</div>
                <label className="md:col-span-4 text-xs text-[#6B6B6B]">Plan
                  <select value={p.cle} onChange={(e) => majPlan(i, { cle: e.target.value })} className="block w-full mt-1 text-sm px-2 py-1.5 border border-[#E0E0DB] bg-white" data-testid={`upload-plan-famille-${i}`}>
                    <option value="nouveau">Nouveau plan</option>
                    {famillesPlans.map((f) => (
                      <option key={f.cle} value={f.cle}>Nouvel indice de : {f.titre}{f.indice_courant ? ` (ind. ${f.indice_courant})` : ''}</option>
                    ))}
                  </select>
                </label>
                <label className="md:col-span-2 text-xs text-[#6B6B6B]">Indice <span className="text-[#C53030]">*</span>
                  <input value={p.indice} onChange={(e) => majPlan(i, { indice: e.target.value.toUpperCase().slice(0, 6) })} required
                    className={`block w-full mt-1 text-sm px-2 py-1.5 border ${indiceDejaDepose(p) ? 'border-[#C53030]' : 'border-[#E0E0DB]'}`} data-testid={`upload-plan-indice-${i}`} />
                  {indiceDejaDepose(p) && <span className="block mt-1 text-[#C53030]" data-testid={`upload-plan-indice-existe-${i}`}>Indice déjà déposé pour ce plan</span>}
                </label>
                {p.cle === 'nouveau' ? (
                  <label className="md:col-span-3 text-xs text-[#6B6B6B]">Lot
                    <select value={p.lot} onChange={(e) => majPlan(i, { lot: e.target.value })} className="block w-full mt-1 text-sm px-2 py-1.5 border border-[#E0E0DB] bg-white" data-testid={`upload-plan-lot-${i}`}>
                      <option value="">Plan général (tous les lots)</option>
                      {lotsMarche.map((l) => <option key={l.id} value={l.id}>{[l.code, l.nom].filter(Boolean).join(' · ')}</option>)}
                    </select>
                  </label>
                ) : <span className="md:col-span-3 text-xs text-[#9A9A9A]">Lot et diffusion repris de l'indice précédent (sauf diffusion choisie ci-dessus)</span>}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" className="rounded-none" onClick={() => setPlansEnAttente(null)}>Annuler</Button>
            <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" disabled={uploading} onClick={deposerPlans} data-testid="upload-plans-deposer">Déposer</Button>
          </div>
        </div>
      )}
      {showAdvanced && (
        <div className="mt-3 border border-[#E0E0DB] rounded-sm p-4 bg-[#FAFAF7]" data-testid="advanced-upload-panel" onClick={e => e.stopPropagation()}>
          <h4 className="text-xs uppercase tracking-wide text-[#6B6B6B] font-medium mb-3">
            Paramètres avancés
          </h4>
          <DocumentVisibilityForm
            visibleTo={visibleTo}
            setVisibleTo={setVisibleTo}
            visibleToSpecificUsers={visibleToSpecificUsers}
            setVisibleToSpecificUsers={setVisibleToSpecificUsers}
            note={advancedNote}
            setNote={setAdvancedNote}
            chantierId={chantierId}
            testIdPrefix="advanced-upload"
          />
          <p className="text-xs text-[#6B6B6B] mt-3 italic">
            ⓘ Ces paramètres s'appliquent à vos prochains dépôts sur ce chantier, jusqu'à ce que vous les changiez.
          </p>
        </div>
      )}
    </div>
  );
}
